import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useDownloadUserAccount } from "./DownoadUserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { updateDownloadUserAcc, deleteDownloadUserAcc } from "./utils";
import { checkPasswordStrength } from "./ResetPassword/utils";

const DownloadUserInfo = () => {
  const [open, setOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [passStrength, setPassStrength] = useState(null);
  const [pwdSuggestion, setPwdSuggestion] = useState(null);
  const [strength, setStrength] = useState({ label: "", score: 0 });
  const [passwordApiResult, setPasswordApiResult] = useState(null);
  const {
    downloadUserAccountInfo,
    selectedDownloadUserAccountInfo,
    setDownloadUserAccountInfo,
    setSelectedDownloadUserAccountInfo,
    setReload,
    authenticationChecked,
  } = useDownloadUserAccount();

  useEffect(() => {
    setOpen(downloadUserAccountInfo !== null);
  }, [downloadUserAccountInfo, setDownloadUserAccountInfo]);

  const formValidate = (selectedDownloadUserAccountInfo) => {
    const newErrors = {};
    if (!selectedDownloadUserAccountInfo.login)
      newErrors.login = "Login is required";
    const password = selectedDownloadUserAccountInfo.password;
    if (changePassword) {
      if (!password) {
        newErrors.password = "Please enter a new password";
      } else if (passwordApiResult === null) {
        newErrors.password = "Password is not validated yet.";
      } else if (!passwordApiResult.valid) {
        newErrors.password = passwordApiResult.details
          ? `${passwordApiResult.details}; ${passwordApiResult.message}`
          : passwordApiResult.message ||
            "Password does not meet security requirements";
      }
    }
    if (
      !selectedDownloadUserAccountInfo.email ||
      !/\S+@\S+\.\S+/.test(selectedDownloadUserAccountInfo.email)
    )
      newErrors.email = "Enter a valid email";
    if (!selectedDownloadUserAccountInfo.name)
      newErrors.name = "Full name is required";

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updtUserAcc = (selectedDownloadUserAccountInfo) => {
    if (formValidate(selectedDownloadUserAccountInfo)) {
      const payload = { ...selectedDownloadUserAccountInfo };
      if (!changePassword) delete payload.password;
      updateDownloadUserAcc(payload).then(() => {
        setSelectedDownloadUserAccountInfo(null);
        setDownloadUserAccountInfo(null);
        setReload(true);
        resetPasswordState();
      });
    }
  };

  const delUserAcc = (selectedDownloadUserAccountInfo) => {
    deleteDownloadUserAcc(selectedDownloadUserAccountInfo).then(() => {
      setSelectedDownloadUserAccountInfo(null);
      setDownloadUserAccountInfo(null);
      setReload(true);
    });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setDownloadUserAccountInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    let address = downloadUserAccountInfo.address;
    address[name] = value;
    setDownloadUserAccountInfo((prev) => ({ ...prev, ["address"]: address }));
  };

  const updateStrength = (password) => {
    if (!password) {
      setStrength({ label: "", score: 0 });
      return;
    }
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    let label = "Very Weak";
    if (score >= 4) label = "Strong";
    else if (score === 3) label = "Good";
    else if (score === 2) label = "Fair";
    else if (score === 1) label = "Weak";
    setStrength({ label, score });
  };

  const getStrengthColor = () => {
    if (strength.label === "Very Weak" || strength.label === "Weak")
      return "error";
    if (strength.label === "Fair" || strength.label === "Good")
      return "warning";
    if (strength.label === "Strong") return "success";
    return "inherit";
  };

  const resetPasswordState = () => {
    setPassStrength(null);
    setPwdSuggestion(null);
    setStrength({ label: "", score: 0 });
    setPasswordApiResult(null);
    setChangePassword(false);
  };

  const handlePasswordChange = async (e) => {
    const value = e.target.value;
    setDownloadUserAccountInfo((prev) => ({ ...prev, password: value }));
    setFormErrors((prev) => ({ ...prev, password: undefined }));
    if (!value) {
      resetPasswordState();
      return;
    }
    updateStrength(value);
    if (value.length >= 3) {
      const result = await checkPasswordStrength(
        value,
        setPassStrength,
        setPwdSuggestion,
      );
      setPasswordApiResult(result);
    } else {
      setPassStrength(null);
      setPwdSuggestion(null);
      setPasswordApiResult(null);
    }
  };

  const onClose = (e, reason) => {
    setSelectedDownloadUserAccountInfo(null);
    setFormErrors({});
    resetPasswordState();
  };

  if (authenticationChecked !== null) {
    const isAccessDenied =
      authenticationChecked.includes("Access denied") ||
      authenticationChecked.includes("not authorized") ||
      authenticationChecked.includes("403") ||
      authenticationChecked.includes("FORBIDDEN");

    const isAuthRequired =
      authenticationChecked.includes("Authentication required") ||
      authenticationChecked.includes("Login expired") ||
      authenticationChecked.includes("session") ||
      authenticationChecked.includes("expired") ||
      authenticationChecked.includes("401") ||
      authenticationChecked.includes("UNAUTHORIZED");

    const wasLoggedIn = sessionStorage.getItem("isUser") === "true";
    const isAdminLoggedIn = sessionStorage.getItem("isAdmin") === "true";

    if (isAccessDenied && isAdminLoggedIn) {
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          Unauthorized. Please{" "}
          <Link to="/About/UserLogin" className="hyper-links">
            <b>Login with Download User credentials</b>
          </Link>{" "}
          to view this page.
        </Typography>
      );
    } else if (isAccessDenied || (isAuthRequired && isAdminLoggedIn)) {
      return <Navigate to="/About/UserLogin" replace />;
    } else if (isAuthRequired && wasLoggedIn) {
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          Session expired.{" "}
          <Link to="/About/UserLogin" className="hyper-links">
            <b>Click here to log back in</b>
          </Link>
        </Typography>
      );
    } else if (isAuthRequired) {
      return <Navigate to="/About/UserLogin" replace />;
    } else {
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h5">
          {authenticationChecked}{" "}
        </Typography>
      );
    }
  }
  return (
    downloadUserAccountInfo && (
      <Card sx={{ borderRadius: 3, width: 1000 }}>
        <CardHeader
          title={
            <font className="layer-title">
              {" "}
              Edit Download User Account Information{" "}
            </font>
          }
          align="center"
          className={"styledtablerow"}
        ></CardHeader>
        <CardContent
          sx={{
            paddingTop: 1,
            paddingLeft: 0,
            paddingRight: 0,
            overflow: "auto",
          }}
        >
          <Box
            component="form"
            size="small"
            sx={{
              "& .MuiTextField-root": { width: "35ch" },
              paddingTop: 3,
              align: "center",
            }}
          >
            <Grid container spacing={{ xs: 1 }} sx={{ paddingLeft: 25 }}>
              <Grid>
                <Table size="small">
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-login"
                        label="Login"
                        name="login"
                        value={downloadUserAccountInfo.login}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={changePassword}
                            onChange={(e) => {
                              setChangePassword(e.target.checked);
                              if (!e.target.checked) {
                                setDownloadUserAccountInfo((prev) => ({
                                  ...prev,
                                  password: "",
                                }));
                                resetPasswordState();
                                setChangePassword(false);
                              }
                            }}
                          />
                        }
                        label="Change Password"
                      />
                      {changePassword && (
                        <Box sx={{ width: "100%", maxWidth: "35ch" }}>
                          <TextField
                            size="small"
                            id="download-user-account-password"
                            label="New Password"
                            name="password"
                            type="password"
                            value={downloadUserAccountInfo.password || ""}
                            onChange={handlePasswordChange}
                            error={!!formErrors.password}
                            helperText={formErrors.password}
                            fullWidth
                          />
                          {(passStrength || strength.score > 0) && (
                            <Box sx={{ mt: 1, mb: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(strength.score / 4) * 100}
                                color={getStrengthColor()}
                                sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                              />
                              <Typography
                                variant="caption"
                                fontSize={10}
                                sx={{ display: "block" }}
                              >
                                Strength: {strength.label}
                              </Typography>
                            </Box>
                          )}
                          {pwdSuggestion && (
                            <Box
                              sx={{
                                mt: 0.5,
                                p: 1,
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                borderRadius: 1,
                                maxHeight: "60px",
                                overflow: "auto",
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontSize={9}
                                color="textSecondary"
                                sx={{
                                  display: "block",
                                  lineHeight: 1.2,
                                  wordBreak: "break-word",
                                }}
                              >
                                {pwdSuggestion}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-name"
                        label="Name"
                        name="name"
                        value={downloadUserAccountInfo.name}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-email"
                        label="Email"
                        name="email"
                        value={downloadUserAccountInfo.email}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-company"
                        label="Organization"
                        name="company"
                        value={downloadUserAccountInfo.company}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-phonenumber"
                        label="Phone Number"
                        name="phoneNumber"
                        value={downloadUserAccountInfo.phoneNumber}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-jobtitle"
                        label="Job Title"
                        name="jobTitle"
                        value={downloadUserAccountInfo.jobTitle}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-streetaddress"
                        label="Street Address"
                        name="street"
                        value={downloadUserAccountInfo.address?.street || ""}
                        onChange={handleAddressChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-city"
                        label="City"
                        name="city"
                        value={downloadUserAccountInfo.address?.city || ""}
                        onChange={handleAddressChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-state"
                        label="State"
                        name="state"
                        value={downloadUserAccountInfo.address?.state || ""}
                        onChange={handleAddressChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-zip"
                        label="Zip Code"
                        name="zip"
                        value={downloadUserAccountInfo.address?.zip || ""}
                        onChange={handleAddressChange}
                      />
                    </TableCell>
                  </TableRow>
                </Table>
              </Grid>
              <Grid>
                <Table size="small">
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-faxnumber"
                        label="Fax Number"
                        name="faxNumber"
                        value={downloadUserAccountInfo.faxNumber}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-intendedLocation"
                        label="Intended Location"
                        name="intendedLocation"
                        value={downloadUserAccountInfo.intendedLocation}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-intendedUsage"
                        label="Intended Usage"
                        name="intendedUsage"
                        value={downloadUserAccountInfo.intendedUsage}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-ipAddress"
                        label="IP Address"
                        name="ipAddress"
                        value={downloadUserAccountInfo.ipAddress}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={downloadUserAccountInfo.requestLink}
                              onChange={(e) => {
                                setDownloadUserAccountInfo(
                                  (downloadUserAccountInfo) => {
                                    return {
                                      ...downloadUserAccountInfo,
                                      requestLink:
                                        !downloadUserAccountInfo.requestLink,
                                    };
                                  },
                                );
                              }}
                            />
                          }
                          label="Request Link"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={downloadUserAccountInfo.requestXml}
                              onChange={(e) => {
                                setDownloadUserAccountInfo(
                                  (downloadUserAccountInfo) => {
                                    return {
                                      ...downloadUserAccountInfo,
                                      requestXml:
                                        !downloadUserAccountInfo.requestXml,
                                    };
                                  },
                                );
                              }}
                            />
                          }
                          label="Request XML"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={downloadUserAccountInfo.revenue}
                              onChange={(e) => {
                                setDownloadUserAccountInfo(
                                  (downloadUserAccountInfo) => {
                                    return {
                                      ...downloadUserAccountInfo,
                                      revenue: !downloadUserAccountInfo.revenue,
                                    };
                                  },
                                );
                              }}
                            />
                          }
                          label="Revenue"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="download-user-account-businessPartners"
                        label="Business Partners"
                        name="businessPartners"
                        value={downloadUserAccountInfo.businessPartners}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                </Table>
              </Grid>
            </Grid>
          </Box>
          <Table>
            <TableRow>
              <TableCell align="right">
                <Button
                  name="Save"
                  label="Save"
                  variant="outlined"
                  size="small"
                  onClick={(e) => updtUserAcc(downloadUserAccountInfo)}
                >
                  Save
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  name="Delete"
                  label="Delete"
                  variant="outlined"
                  size="small"
                  onClick={(e) => delUserAcc(downloadUserAccountInfo)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          </Table>
        </CardContent>
      </Card>
    )
  );
};

export default DownloadUserInfo;
